// Coarse-bucket relative-age qualifier for a date — used to render
// parenthetical clarifications like "Aug 5, 2026 (3 months ago)" alongside
// an absolute date. Designed to read as an at-a-glance answer to "how
// recent is this?" without requiring date math from the operator.
//
// Buckets (matching the precision a clinician scans for):
//   same day  → "today"
//   ±1 day    → "yesterday" / "tomorrow"
//   2–13 days → "N days ago" / "in N days"
//   14–59     → "N weeks ago" / "in N weeks"
//   60–364    → "N months ago" / "in N months"
//   365+      → "N years ago" / "in N years"
//
// Beyond 7 days canon (date-time.mdx) prefers shortened-absolute alone, but
// we pair both because "how long ago" is the operational scan-question for
// past procedures / measurements and the absolute date keeps the precision.
export function getRelativeAge(date: string | Date | undefined | null): string | null {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const isPast = diffMs >= 0;
  const absDays = Math.floor(Math.abs(diffMs) / 86400000);
  const suffix = isPast ? ' ago' : '';
  const prefix = isPast ? '' : 'in ';

  if (absDays === 0) return 'today';
  if (absDays === 1) return isPast ? 'yesterday' : 'tomorrow';
  if (absDays < 14) return `${prefix}${absDays} days${suffix}`.trim();
  if (absDays < 60) {
    const weeks = Math.round(absDays / 7);
    return `${prefix}${weeks} week${weeks === 1 ? '' : 's'}${suffix}`.trim();
  }
  if (absDays < 365) {
    const months = Math.round(absDays / 30);
    return `${prefix}${months} month${months === 1 ? '' : 's'}${suffix}`.trim();
  }
  const years = Math.round(absDays / 365);
  return `${prefix}${years} year${years === 1 ? '' : 's'}${suffix}`.trim();
}

// Duration between two dates ("over 8 months", "over 2 weeks"). Used on the
// TWL row of Body measurements to convey the journey length without two
// parens on the Weight row (one for each endpoint's age). Returns null if
// either date is missing or end ≤ start (no journey to report).
export function getJourneyDuration(
  start: string | Date | undefined | null,
  end: string | Date | undefined | null
): string | null {
  if (!start || !end) return null;
  const s = typeof start === 'string' ? new Date(start) : start;
  const e = typeof end === 'string' ? new Date(end) : end;
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const diffMs = e.getTime() - s.getTime();
  if (diffMs <= 0) return null;
  const days = Math.floor(diffMs / 86400000);
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 60) {
    const weeks = Math.round(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? '' : 's'}`;
}
